import { useAuthStore } from '../stores/authStore';
import type { PermissionKey } from '@repo/types';

const SUPER_ADMIN_ROLE = 'Super Admin';

export function usePermission() {
  const user = useAuthStore((s) => s.user);

  /**
   * Returns true if the current user has the given permission.
   * SUPER_ADMIN bypasses all permission checks.
   */
  const can = (permission: PermissionKey): boolean => {
    if (!user) return false;
    if (isSuperAdmin()) return true;
    return user.permissions.includes(permission);
  };

  /**
   * Returns true if the current user has the SUPER_ADMIN role.
   */
  const isSuperAdmin = (): boolean => {
    if (!user) return false;
    return user.roles.includes(SUPER_ADMIN_ROLE);
  };

  /**
   * Returns true if the current user has ANY of the given permissions.
   */
  const canAny = (...permissions: PermissionKey[]): boolean => {
    if (!user) return false;
    if (isSuperAdmin()) return true;
    return permissions.some((p) => user.permissions.includes(p));
  };

  /**
   * Returns true if the current user has ALL of the given permissions.
   */
  const canAll = (...permissions: PermissionKey[]): boolean => {
    if (!user) return false;
    if (isSuperAdmin()) return true;
    return permissions.every((p) => user.permissions.includes(p));
  };

  return { can, canAny, canAll, isSuperAdmin };
}
